import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Phone,
  MapPin,
  Search,
  Menu,
  X,
  ChevronDown,
  Truck,
  Star,
} from "lucide-react";
import { NAV, SITE } from "@/mock";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/mattresses?q=${encodeURIComponent(query)}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top utility bar */}
      <div className="bg-neutral-900 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 text-xs sm:px-6 lg:px-8">
          <span className="flex items-center gap-2 font-medium">
            <Truck className="h-4 w-4 text-[#ff3b52]" aria-hidden="true" />
            Same-Day Delivery, Set-Up &amp; Removal
          </span>
          <div className="flex items-center gap-4">
            <a href={SITE.address.mapsUrl} target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1 hover:text-[#ff3b52] md:flex">
              <MapPin className="h-4 w-4 text-[#ff3b52]" aria-hidden="true" />
              {SITE.address.full}
            </a>
            <a href={SITE.googleReviewUrl} target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1 hover:text-[#ff3b52] sm:flex">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" />
              {SITE.rating.toFixed(1)} · {SITE.reviewCount} Reviews
            </a>
            <a href={SITE.phoneHref} className="flex items-center gap-1 font-semibold hover:text-[#ff3b52]">
              <Phone className="h-4 w-4" aria-hidden="true" />
              {SITE.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <button
            type="button"
            className="lg:hidden"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-7 w-7 text-neutral-900" aria-hidden="true" />
          </button>

          <Link to="/" className="flex shrink-0 items-center" aria-label="MattressTown home">
            <img src={SITE.logo} alt="MattressTown" className="h-9 w-auto sm:h-11" />
          </Link>

          <form onSubmit={onSearch} role="search" className="ml-auto hidden max-w-md flex-1 items-center lg:flex">
            <label htmlFor="site-search" className="sr-only">Search products</label>
            <div className="relative w-full">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" aria-hidden="true" />
              <input
                id="site-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search mattresses, brands, sizes..."
                className="w-full rounded-full border border-neutral-300 bg-neutral-50 py-2.5 pl-11 pr-4 text-sm outline-none focus:border-[#D0021B]"
              />
            </div>
          </form>

          <a
            href={SITE.address.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto hidden items-center gap-2 rounded-full bg-[#D0021B] px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016] lg:flex"
          >
            <MapPin className="h-4 w-4" aria-hidden="true" /> Visit Showroom
          </a>
          <a href={SITE.phoneHref} className="ml-auto rounded-full bg-[#D0021B] p-2.5 text-white lg:hidden" aria-label={`Call ${SITE.phone}`}>
            <Phone className="h-5 w-5" aria-hidden="true" />
          </a>
        </div>

        {/* Desktop nav */}
        <nav aria-label="Primary" className="hidden border-t border-neutral-200 lg:block">
          <ul className="mx-auto flex max-w-7xl items-center gap-1 px-4 sm:px-6 lg:px-8">
            {NAV.map((item) => (
              <li
                key={item.to}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-4 py-3 text-sm font-bold uppercase tracking-wide transition-colors hover:text-[#D0021B] ${
                      isActive ? "text-[#D0021B]" : "text-neutral-800"
                    }`
                  }
                >
                  {item.label}
                  {item.children && <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                </NavLink>
                {item.children && openMenu === item.label && (
                  <ul className="absolute left-0 top-full z-50 w-64 rounded-b-xl border border-neutral-200 bg-white py-2 shadow-xl">
                    {item.children.map((c) => (
                      <li key={c.to}>
                        <Link
                          to={c.to}
                          className="block px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:text-[#D0021B]"
                        >
                          {c.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div className="absolute left-0 top-0 h-full w-80 max-w-[85vw] overflow-y-auto bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <img src={SITE.logo} alt="MattressTown" className="h-8 w-auto" />
              <button type="button" aria-label="Close menu" onClick={() => setMobileOpen(false)}>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <form onSubmit={onSearch} role="search" className="mb-4">
              <label htmlFor="m-search" className="sr-only">Search products</label>
              <input
                id="m-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="w-full rounded-full border border-neutral-300 bg-neutral-50 px-4 py-2.5 text-sm outline-none focus:border-[#D0021B]"
              />
            </form>
            <ul className="space-y-1">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-lg px-3 py-2.5 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-100"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <ul className="ml-3 border-l border-neutral-200 pl-3">
                      {item.children.map((c) => (
                        <li key={c.to}>
                          <Link
                            to={c.to}
                            onClick={() => setMobileOpen(false)}
                            className="block rounded px-3 py-2 text-sm text-neutral-600 hover:text-[#D0021B]"
                          >
                            {c.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}
